import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api";

/**
 * v2.0.12 — Auto-Translation dynamique du DOM entier via LLM.
 *
 * Stratégie :
 * 1. À chaque changement de langue (ou navigation), scanne tous les textNodes visibles du body.
 * 2. Envoie les strings uniques au backend `POST /api/i18n/translate-batch`.
 * 3. Backend renvoie un dict {source: translation} (cache DB + LLM Claude Sonnet 4.6).
 * 4. Écrit `.nodeValue = translation` sur les textNodes correspondants.
 * 5. MutationObserver capture les changements React ultérieurs pour re-traduire.
 *
 * Skip : inputs, script, style, code, [translate="no"], [data-no-translate],
 * strings numériques/monétaires/codes, éléments avec data-testid contenant "code|price|amount".
 *
 * Idempotence : les traductions déjà appliquées sont mémorisées en RAM pour ne pas
 * bombarder l'API à chaque mutation.
 */

const MAX_NODES_PER_PASS = 400;

const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "IFRAME", "SVG", "PATH",
  "INPUT", "TEXTAREA", "SELECT", "OPTION", "CODE", "PRE", "KBD", "SAMP", "TIME",
]);

// Regex : strings qu'on ne traduit pas (codes, monnaie, dates, IDs, URLs, emails)
const SKIP_PATTERNS = [
  /^\s*$/,                                    // vide
  /^[\d\s.,+\-€$£₹¥%×x/:]+$/,                 // numérique/monétaire
  /^PAG-[A-Z]+-\d+$/,                         // codes PAG
  /^[A-Z0-9]{2,}(-[A-Z0-9]+)+$/,              // codes uppercase
  /^https?:\/\//,                             // URLs
  /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i, // emails
  /^\d{2}[/-]\d{2}[/-]\d{2,4}$/,              // dates
];

function shouldSkipString(text) {
  if (!text || text.length < 2 || text.length > 500) return true;
  const t = text.trim();
  if (t.length < 2) return true;
  return SKIP_PATTERNS.some((re) => re.test(t));
}

function shouldSkipElement(el) {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return false;
  if (SKIP_TAGS.has(el.tagName)) return true;
  if (el.getAttribute("translate") === "no") return true;
  if (el.hasAttribute("data-no-translate")) return true;
  // Phase 82 — sur un mini-site public (.wsite), les toasts restent dans la langue du site
  if (el.hasAttribute("data-sonner-toaster") && document.querySelector(".wsite")) return true;
  const tid = el.getAttribute("data-testid") || "";
  if (/code|price|amount|iban|siret|token|secret|key|webhook|money|pricing-value|pricing-slider|plan-price/i.test(tid)) return true;
  if (el.classList && (el.classList.contains("font-mono") || el.classList.contains("no-translate"))) return true;
  return false;
}

function collectTextNodes(root, out, seen) {
  if (!root) return;
  if (shouldSkipElement(root)) return;
  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      // v2.0.20 — Si React a réécrit ce nœud depuis notre dernière traduction,
      // la NOUVELLE valeur devient la source : sinon on réappliquait l'ancienne
      // traduction en cache et les données dynamiques restaient figées à l'écran.
      if (child.__i18n_written !== undefined && child.nodeValue !== child.__i18n_written) {
        child.__i18n_original = child.nodeValue;
        child.__i18n_written = undefined;
        child.__i18n_lang = undefined;
        child.__i18n_source = undefined;
      }
      const original = child.__i18n_original || child.nodeValue;
      const trimmed = original?.trim();
      if (!trimmed || shouldSkipString(trimmed)) continue;
      // Skip si déjà traduit dans la langue courante
      if (child.__i18n_lang === seen.currentLang && child.__i18n_source === trimmed) continue;
      out.push({ node: child, original: trimmed, raw: original });
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      collectTextNodes(child, out, seen);
    }
  }
}

export default function AutoTranslator() {
  const { i18n } = useTranslation();
  const currentLangRef = useRef(i18n.resolvedLanguage);
  const pendingRef = useRef(null);         // timeout id du debounce
  const inFlightRef = useRef(new Set());   // texts en cours
  const observerRef = useRef(null);
  // Cache RAM : { "text|target": "translation" }
  const memRef = useRef(new Map());

  const runTranslationPass = useCallback(async (root = document.body) => {
    const target = (i18n.resolvedLanguage || "fr").split("-")[0];
    if (target === "fr") {
      // Restaurer les originaux si on revient à fr
      restoreOriginals(document.body);
      return;
    }
    let items = [];
    collectTextNodes(root, items, { currentLang: target });
    if (items.length === 0) return;
    // Plafond par passe : le reste est traité à la passe suivante (évite de figer l'UI sur les grosses pages)
    if (items.length > MAX_NODES_PER_PASS) {
      items = items.slice(0, MAX_NODES_PER_PASS);
      setTimeout(() => schedulePassRef.current?.(), 50);
    }

    // Dedup par source
    const strings = [];
    const seen = new Set();
    for (const it of items) {
      if (seen.has(it.original)) continue;
      // Skip si en cache RAM
      const memKey = `${it.original}|${target}`;
      if (memRef.current.has(memKey)) continue;
      if (inFlightRef.current.has(memKey)) continue;
      seen.add(it.original);
      strings.push(it.original);
      inFlightRef.current.add(memKey);
    }

    // 1. Applique d'abord ce qu'on a déjà en cache RAM (rapide)
    for (const it of items) {
      const memKey = `${it.original}|${target}`;
      const cached = memRef.current.get(memKey);
      if (cached && cached !== it.original) {
        applyTranslation(it.node, it.raw, cached, target);
      }
    }

    if (strings.length === 0) return;

    // 2. Fetch les manquants
    try {
      const { data } = await api.post("/i18n/translate-batch", {
        source: "fr",
        target,
        strings,
      });
      const map = data.translations || {};
      // Store en cache RAM
      for (const [k, v] of Object.entries(map)) {
        memRef.current.set(`${k}|${target}`, v);
      }
      // Applique
      for (const it of items) {
        const tr = map[it.original];
        if (tr && tr !== it.original) applyTranslation(it.node, it.raw, tr, target);
      }
    } catch (e) {
      // silent
    } finally {
      for (const s of strings) inFlightRef.current.delete(`${s}|${target}`);
    }
  }, [i18n.resolvedLanguage]);

  const schedulePass = useCallback(() => {
    if (pendingRef.current) clearTimeout(pendingRef.current);
    pendingRef.current = setTimeout(() => { pendingRef.current = null; runTranslationPass(); }, 350);
  }, [runTranslationPass]);
  const schedulePassRef = useRef(null);
  schedulePassRef.current = schedulePass;

  // Passe initiale + à chaque changement de langue
  useEffect(() => {
    currentLangRef.current = (i18n.resolvedLanguage || "fr").split("-")[0];
    schedulePass();
    const handler = () => { currentLangRef.current = (i18n.resolvedLanguage || "fr").split("-")[0]; schedulePass(); };
    i18n.on("languageChanged", handler);
    return () => { i18n.off("languageChanged", handler); };
  }, [i18n, schedulePass]);

  // MutationObserver global : rescan à chaque changement DOM
  useEffect(() => {
    const target = document.body;
    if (!target) return;
    const obs = new MutationObserver((mutations) => {
      // Optim : ignore si toutes les mutations concernent nos propres écritures
      let significant = false;
      let toaster = null;
      for (const m of mutations) {
        if (m.type === "childList" && m.addedNodes.length) {
          significant = true;
          // Toasts (sonner) : traduction immédiate, sans debounce, limitée au conteneur des toasts
          const el = m.target?.nodeType === Node.ELEMENT_NODE ? m.target : m.target?.parentElement;
          toaster = toaster || el?.closest?.("[data-sonner-toaster]") || null;
        }
        if (m.type === "characterData") {
          const node = m.target;
          if (!node.__i18n_from_translator) { significant = true; }
        }
      }
      if (toaster) runTranslationPass(toaster);
      if (significant) schedulePass();
    });
    obs.observe(target, { childList: true, subtree: true, characterData: true });
    observerRef.current = obs;
    return () => obs.disconnect();
  }, [schedulePass, runTranslationPass]);

  return null; // composant invisible
}

// ---------- Helpers ----------

function applyTranslation(node, rawOriginal, translation, targetLang) {
  if (!node || node.nodeType !== Node.TEXT_NODE) return;
  // Mémorise l'original pour pouvoir restaurer
  node.__i18n_original = rawOriginal;
  // Préserve leading/trailing whitespace de l'original pour ne pas casser l'inline layout
  const leading = rawOriginal.match(/^\s*/)?.[0] || "";
  const trailing = rawOriginal.match(/\s*$/)?.[0] || "";
  node.__i18n_from_translator = true;
  node.nodeValue = leading + translation + trailing;
  node.__i18n_from_translator = false;
  // Empreinte de ce que NOUS avons écrit : toute divergence future = mise à jour React
  node.__i18n_written = node.nodeValue;
  node.__i18n_lang = targetLang;
  node.__i18n_source = rawOriginal.trim();
}

function restoreOriginals(root) {
  if (!root) return;
  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      if (child.__i18n_original && child.__i18n_written !== undefined
          && child.nodeValue === child.__i18n_written) {
        child.__i18n_from_translator = true;
        child.nodeValue = child.__i18n_original;
        child.__i18n_from_translator = false;
      }
      child.__i18n_written = undefined;
      child.__i18n_lang = "fr";
      child.__i18n_source = undefined;
      child.__i18n_original = undefined;
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      restoreOriginals(child);
    }
  }
}
